import { SlashCommandBuilder, SlashCommandOptionsOnlyBuilder, SlashCommandSubcommandsOnlyBuilder, ChatInputCommandInteraction, AutocompleteInteraction, ContextMenuCommandBuilder, MessageContextMenuCommandInteraction, UserContextMenuCommandInteraction, Message, ButtonInteraction, ModalSubmitInteraction } from "discord.js";
import { AdvancedCommandOptions, MessageArguments, IButtonCommand, IButtonCommandOptions, IContextMenuCommand, IContextMenuCommandOptions, IDBasedCommand, IMessageCommand, IMessageCommandOptions, IModalCommand, IModalCommandOptions, ISelectMenuCommand, ISelectMenuCommandOptions, ISlashCommand, ISlashCommandOptions, MessageCommandOptions, SelectMenus } from "../typings/commands";

export class MessageCommand<T extends string> implements IMessageCommand<T> {
    constructor(options: IMessageCommandOptions<T>) {
        Object.assign(this, options);
    }
    commandType = "message" as const;
    data: MessageCommandOptions<T>;
    run: (message: Message, args: MessageArguments<T>) => any | Promise<any>;
    enabled?: boolean | undefined;
    options?: AdvancedCommandOptions | undefined;
    isIDBased(): this is IDBasedCommand<any> {
        return false
    }
    isSlashCommand(): this is ISlashCommand {
        return false
    }
    isContextMenuCommand(): this is IContextMenuCommand<"user" | "message"> {
        return false
    }
    isMessageCommand(): this is IMessageCommand<T> {
        return true
    }
}

export class SlashCommand implements ISlashCommand {
    constructor(options: ISlashCommandOptions) {
        Object.assign(this, options);
    }
    commandType = "slash" as const;
    data: SlashCommandBuilder | SlashCommandOptionsOnlyBuilder | SlashCommandSubcommandsOnlyBuilder;
    run: (interaction: ChatInputCommandInteraction) => any | Promise<any>;
    autocomplete?: Record<string, (interaction: AutocompleteInteraction, value: string) => any | Promise<any>> | undefined;
    enabled?: boolean | undefined;
    options?: AdvancedCommandOptions | undefined;
    isIDBased(): this is IDBasedCommand<any> {
        return false
    }
    isSlashCommand(): this is ISlashCommand {
        return true
    }
    isContextMenuCommand(): this is IContextMenuCommand<"user" | "message"> {
        return false
    }
    isMessageCommand(): this is IMessageCommand<any> {
        return false
    }
}

export class ContextMenuCommand<T extends "user" | "message"> implements IContextMenuCommand<T> {
    constructor(options: IContextMenuCommandOptions<T>) {
        Object.assign(this, options);
    }
    commandType: T extends "user" ? "contextMenuUser" : "contextMenuMessage";
    data: ContextMenuCommandBuilder;
    type: T;
    run: (interaction: T extends "user" ? UserContextMenuCommandInteraction : MessageContextMenuCommandInteraction) => any | Promise<any>;
    enabled?: boolean | undefined;
    options?: AdvancedCommandOptions | undefined;
    isIDBased(): this is IDBasedCommand<any> {
        return false
    }
    isSlashCommand(): this is ISlashCommand {
        return false
    }
    isContextMenuCommand(): this is IContextMenuCommand<"user" | "message"> {
        return true
    }
    isUserContextMenuCommand(): this is IContextMenuCommand<"user"> {
        return this.type === "user"
    }
    isMessageContextMenuCommand(): this is IContextMenuCommand<"message"> {
        return this.type === "message"
    }
    isMessageCommand(): this is IMessageCommand<any> {
        return false
    }
}

export class SelectMenuCommand<X extends keyof SelectMenus, T extends string[]> implements ISelectMenuCommand<X, T> {
    constructor(options: ISelectMenuCommandOptions<X, T>) {
        Object.assign(this, options);
    }
    commandType = "selectMenu" as const;
    run: (interaction: SelectMenus[X], args: Record<T[number], string | undefined>) => any | Promise<any>;
    id: string;
    type: X;
    user?: boolean | undefined;
    arguments?: T | undefined;
    enabled?: boolean | undefined;
    options?: AdvancedCommandOptions | undefined;
    isIDBased(): this is IDBasedCommand<any> {
        return true
    }
    isSlashCommand(): this is ISlashCommand {
        return false
    }
    isContextMenuCommand(): this is IContextMenuCommand<"user" | "message"> {
        return false
    }
    isMessageCommand(): this is IMessageCommand<any> {
        return false
    }
    isSelectMenuCommand(): this is ISelectMenuCommand<X, T> {
        return true
    }
    isButtonCommand(): this is IButtonCommand<T> {
        return false
    }
    isModalCommand(): this is IModalCommand<T> {
        return false
    }
    isStringSelectMenuCommand(): this is ISelectMenuCommand<"string", T> {
        return this.type === "string"
    }
    isUserSelectMenuCommand(): this is ISelectMenuCommand<"user", T> {
        return this.type === "user"
    }
    isChannelSelectMenuCommand(): this is ISelectMenuCommand<"channel", T> {
        return this.type === "channel"
    }
    isRoleSelectMenuCommand(): this is ISelectMenuCommand<"role", T> {
        return this.type === "role"
    }
    isMentionableSelectMenuCommand(): this is ISelectMenuCommand<"mentionable", T> {
        return this.type === "mentionable"
    }
}

export class ButtonCommand<T extends string[]> implements IButtonCommand<T> {
    constructor(options: IButtonCommandOptions<T>) {
        Object.assign(this, options);
    }
    commandType = "button" as const;
    run: (interaction: ButtonInteraction, args: Record<T[number], string | undefined>) => any | Promise<any>;
    id: string;
    user?: boolean | undefined;
    arguments?: T | undefined;
    enabled?: boolean | undefined;
    options?: AdvancedCommandOptions | undefined;
    isIDBased(): this is IDBasedCommand<any> {
        return true
    }
    isSlashCommand(): this is ISlashCommand {
        return false
    }
    isContextMenuCommand(): this is IContextMenuCommand<"user" | "message"> {
        return false
    }
    isMessageCommand(): this is IMessageCommand<any> {
        return false
    }
    isSelectMenuCommand(): this is ISelectMenuCommand<any, T> {
        return false
    }
    isButtonCommand(): this is IButtonCommand<T> {
        return true
    }
    isModalCommand(): this is IModalCommand<T> {
        return false
    }
}

export class ModalCommand<T extends string[]> implements IModalCommand<T> {
    constructor(options: IModalCommandOptions<T>) {
        Object.assign(this, options);
    }
    commandType = "modal" as const;
    run: (interaction: ModalSubmitInteraction, args: Record<T[number], string | undefined>) => any | Promise<any>;
    id: string;
    user?: boolean | undefined;
    arguments?: T | undefined;
    enabled?: boolean | undefined;
    options?: AdvancedCommandOptions | undefined;
    isIDBased(): this is IDBasedCommand<any> {
        return true
    }
    isSlashCommand(): this is ISlashCommand {
        return false
    }
    isContextMenuCommand(): this is IContextMenuCommand<"user" | "message"> {
        return false
    }
    isMessageCommand(): this is IMessageCommand<any> {
        return false
    }
    isSelectMenuCommand(): this is ISelectMenuCommand<any, T> {
        return false
    }
    isButtonCommand(): this is IButtonCommand<T> {
        return false
    }
    isModalCommand(): this is IModalCommand<T> {
        return true
    }
}